import { useState } from "react";
import { useHistory } from "react-router-dom";

import { SearchTvSeriesEntriesRequest } from "../../../common/api/catalogue-api/tv-api/SearchTvSeriesEntriesRequest";
import {
    SearchTvSeriesEntriesResponse,
    TvSeriesEntryResponseItem
} from "../../../common/api/catalogue-api/tv-api/SearchTvSeriesEntriesResponse";
import { useCatalogueTvApi } from "../../../common/api/catalogue-api/tv-api/useCatalogueTvApi";
import { useAssetsApi } from "../../../common/api/master-api/assets/useAssetsApi";
import { SearchAssetsContentAggregationsResponse } from "../../../common/api/master-api/search/content-requests-reposnses/SearchAssetsContentAggregationsResponse";
import { ContentSubtype } from "../../../common/types/ContentSubtype";
import { IdAggregation } from "../../../common/types/IdAggregation";
import { getTvSeriesAssetsListHref } from "../../AssetsRoutes";
import { EpisodeItem, mapToEpisodeItem, mapToTvSeriesSearchItem, TvSeriesSearchItem } from "./TvSeriesSearchItem";
import { TvSeriesSearchItemGroup } from "./TvSeriesSearchItemGroup";

type TvSeriesSearchArgs = { query: string; types: number[] };

export const useTvSeriesSearchListPage = () => {
    const history = useHistory();

    const { searchTvSeriesEntries } = useCatalogueTvApi();

    const { searchAssetsContentAggregations } = useAssetsApi();

    const [items, setItems] = useState<TvSeriesSearchItem[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);

    const [groups, setGroups] = useState<TvSeriesSearchItemGroup[]>([]);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [loadMoreParameters, setLoadMoreParameters] = useState<SearchTvSeriesEntriesRequest>();
    const [cachedAggregations, setCachedAggregations] = useState<SearchAssetsContentAggregationsResponse>({
        aggregations: []
    });
    const [isMoreLoading, setMoreIsLoading] = useState<boolean>(false);

    const pageSize = 50;

    const search = async ({ query, types }: TvSeriesSearchArgs) => {
        if (query) {
            searchByQuery({ query, types });
        } else {
            searchLastModified(types);
        }
    };

    const searchByQuery = async ({ query, types }: { query: string; types: number[] }) => {
        setIsLoading(true);
        setGroups([]);

        const aggregationsResponse = await searchAssetsContentAggregations({
            query,
            subtype: ContentSubtype.TV,
            types
        });
        setCachedAggregations(aggregationsResponse);

        if (!aggregationsResponse.aggregations.length) {
            setIsLoading(false);
            setListEmpty();
            return;
        }

        const episodeIds = aggregationsResponse.aggregations.map((agg) => agg.id);

        searchTvSeriesEntries({ query, page: 1, pageSize, episodeIds })
            .then((response: SearchTvSeriesEntriesResponse) => {
                setTotalCount(response.totalCount);

                const searchItems = mapResponseToSearchItems(response, aggregationsResponse.aggregations);

                const itemGroups = createGroups(searchItems);
                const episodes: EpisodeItem[] = searchItems.filter(isEpisodeItem);
                const sortedEpisodes = getSortedEpisodes(episodes, itemGroups);

                setItems(sortedEpisodes);
                setGroups(itemGroups);

                setLoadMoreParameters({ query, page: 2, pageSize, episodeIds });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const searchLastModified = async (types: number[]) => {
        setIsLoading(true);
        setGroups([]);

        const aggregationsResponse = await searchAssetsContentAggregations({
            subtype: ContentSubtype.TV,
            types
        });
        setCachedAggregations(aggregationsResponse);

        if (!aggregationsResponse.aggregations.length) {
            setIsLoading(false);
            setListEmpty();
            return;
        }

        const episodeIds = aggregationsResponse.aggregations.slice(0, pageSize).map((agg) => agg.id);

        searchTvSeriesByIds(episodeIds)
            .then((response: SearchTvSeriesEntriesResponse) => {
                setTotalCount(aggregationsResponse.aggregations.length);

                const searchItems = mapAndSortResponseToLastModifiedTvSeriesItems(
                    response,
                    aggregationsResponse.aggregations
                );

                const itemGroups = createGroups(searchItems);
                const episodes: EpisodeItem[] = searchItems.filter(isEpisodeItem);
                const sortedEpisodes = getSortedEpisodes(episodes, itemGroups);

                setItems(sortedEpisodes);
                setGroups(itemGroups);

                setLoadMoreParameters({ query: "", page: 2, pageSize, episodeIds });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const loadMore = async () => {
        if (!loadMoreParameters) {
            throw new Error("Parameters for 'load more' must not be empty.");
        }

        if (loadMoreParameters.query) {
            loadMoreByQuery(loadMoreParameters);
        } else {
            loadMoreLastModified(loadMoreParameters);
        }
    };

    const loadMoreByQuery = async (searchParameters: SearchTvSeriesEntriesRequest) => {
        setMoreIsLoading(true);

        searchTvSeriesEntries(searchParameters)
            .then((response: SearchTvSeriesEntriesResponse) => {
                setTotalCount(response.totalCount);

                const searchItems: TvSeriesSearchItem[] = mapResponseToSearchItems(
                    response,
                    cachedAggregations.aggregations
                );

                const itemGroups = createGroups(searchItems);
                const episodes: EpisodeItem[] = searchItems.filter(isEpisodeItem);
                const sortedEpisodes = getSortedEpisodes(episodes, itemGroups, items.length);

                setItems([...items, ...sortedEpisodes]);
                setGroups([...groups, ...itemGroups]);

                setLoadMoreParameters({ ...searchParameters, page: searchParameters.page + 1 });
            })
            .finally(() => {
                setMoreIsLoading(false);
            });
    };

    const loadMoreLastModified = async (searchParameters: SearchTvSeriesEntriesRequest) => {
        setMoreIsLoading(true);

        const episodeIds = cachedAggregations.aggregations
            .slice((searchParameters.page - 1) * pageSize, searchParameters.page * pageSize)
            .map((agg) => agg.id);

        searchTvSeriesByIds(episodeIds)
            .then((response: SearchTvSeriesEntriesResponse) => {
                setTotalCount(cachedAggregations.aggregations.length);

                const searchItems = mapAndSortResponseToLastModifiedTvSeriesItems(
                    response,
                    cachedAggregations.aggregations
                );

                const itemGroups = createGroups(searchItems);
                const episodes: EpisodeItem[] = searchItems.filter(isEpisodeItem);
                const sortedEpisodes = getSortedEpisodes(episodes, itemGroups, items.length);

                setItems([...items, ...sortedEpisodes]);
                setGroups([...groups, ...itemGroups]);

                setLoadMoreParameters({
                    ...searchParameters,
                    page: searchParameters.page + 1
                });
            })
            .finally(() => {
                setMoreIsLoading(false);
            });
    };

    const moveToAssetsListByGroup = (itemGroup: TvSeriesSearchItemGroup) => {
        const href = getTvSeriesAssetsListHref(itemGroup.tvSeriesId, { seasonId: itemGroup.seasonId });
        history.push(href);
    };

    const searchTvSeriesByIds = async (episodeIds: string[]): Promise<SearchTvSeriesEntriesResponse> => {
        return searchTvSeriesEntries({ query: "", pageSize, page: 1, episodeIds });
    };

    const setListEmpty = () => {
        setItems([]);
        setTotalCount(0);
    };

    return {
        items,
        totalCount,
        groups,
        isLoading,
        isMoreLoading,
        pageSize,
        search,
        loadMore,
        moveToAssetsListByGroup
    };
};

const isEpisodeItem = (item: TvSeriesSearchItem): item is EpisodeItem => item.type === "episode";

const createGroups = (searchItems: TvSeriesSearchItem[]): TvSeriesSearchItemGroup[] => {
    const searchItemGroups: TvSeriesSearchItemGroup[] = [];

    searchItems.forEach((item: TvSeriesSearchItem) => {
        const key = createGroupKey(item);
        const name = createGroupName(item);

        const seasonId = item.type !== "tv-series" ? item.seasonId : undefined;

        if (!searchItemGroups.some((group: TvSeriesSearchItemGroup) => group.name === name)) {
            searchItemGroups.push({
                key,
                name,
                type: item.type,
                tvSeriesId: item.tvSeriesId,
                seasonId,
                count: 0,
                startIndex: 0
            });
        }
    });

    return searchItemGroups;
};

const getSortedEpisodes = (
    searchItems: EpisodeItem[],
    groups: TvSeriesSearchItemGroup[],
    previousEpisodesCount = 0
): TvSeriesSearchItem[] => {
    const episodes = searchItems.filter((item: TvSeriesSearchItem) => item.type === "episode");

    let sortedEpisodes: TvSeriesSearchItem[] = [];

    groups.forEach((group: TvSeriesSearchItemGroup) => {
        const groupedEpisodes = episodes.filter((item: EpisodeItem) => createGroupName(item) === group.name);

        group.count = groupedEpisodes.length;
        group.startIndex = sortedEpisodes.length + previousEpisodesCount;

        groupedEpisodes.sort((a: EpisodeItem, b: EpisodeItem) => (a.episodeNumber >= b.episodeNumber ? 1 : -1));

        sortedEpisodes = sortedEpisodes.concat(groupedEpisodes);
    });

    return sortedEpisodes;
};

const createGroupKey = (item: TvSeriesSearchItem): string => {
    const uniqueSuffix = Math.random().toString(36).substr(2, 9);

    if (item.type === "tv-series") {
        return `${item.tvSeriesName} - ${uniqueSuffix}`;
    }

    if (item.type === "season") {
        return `${item.tvSeriesName} - ${item.seasonName} - ${uniqueSuffix}`;
    }

    return `${item.tvSeriesName} - ${item.seasonName} - ${item.episodeId} - ${uniqueSuffix}`;
};

const createGroupName = (item: TvSeriesSearchItem): string => {
    if (item.type === "tv-series") {
        return item.tvSeriesName;
    }

    return `${item.tvSeriesName} - ${item.seasonName}`;
};

const mapResponseToSearchItems = (response: SearchTvSeriesEntriesResponse, aggregations: IdAggregation[]) => {
    return response.items.map((item: TvSeriesEntryResponseItem) => {
        if (item.type === "episode") {
            const agg = aggregations.find((aggregation) => aggregation.id === item.episode.id);

            if (!agg) throw new Error("Aggregateion should not be undefined");

            return mapToTvSeriesSearchItem(item, { assetsCount: agg.count, types: agg.types });
        }
        return mapToTvSeriesSearchItem(item, { assetsCount: 0, types: [] });
    });
};

const mapAndSortResponseToLastModifiedTvSeriesItems = (
    response: SearchTvSeriesEntriesResponse,
    aggregations: IdAggregation[]
) => {
    const tvSeriesSearchItems: EpisodeItem[] = [];

    aggregations.forEach((agg) => {
        const searchItem = response.items.find((item) => item.type === "episode" && item.episode.id === agg.id);
        if (searchItem && searchItem.type === "episode") {
            tvSeriesSearchItems.push(mapToEpisodeItem(searchItem, { assetsCount: agg.count, types: agg.types }));
        }
    });

    return [...tvSeriesSearchItems];
};
